import { IRichTextPendingImage } from "./types";

// Module-level so it survives editor unmount (wizards, modals).
const registry = new Map<string, IRichTextPendingImage>();

export const registerPendingImage = (image: IRichTextPendingImage): string => {
  const src = URL.createObjectURL(image.blob);
  registry.set(src, image);
  return src;
};

export const getPendingImage = (
  src: string
): IRichTextPendingImage | undefined => registry.get(src);

/**
 * Records the storage upload for a pending blob so it is never uploaded
 * twice: a retried submit after a partial failure, and two fields sharing one
 * pasted blob, both reuse this instead of producing another draft file that
 * only the cleanup cron would ever collect.
 */
export const markPendingImageUploaded = (
  src: string,
  uploaded: { url: string; token: string }
): void => {
  const pending = registry.get(src);
  if (pending) {
    registry.set(src, { ...pending, uploaded });
  }
};

export const releasePendingImage = (src: string): void => {
  if (!registry.has(src)) {
    return;
  }
  URL.revokeObjectURL(src);
  registry.delete(src);
};

/**
 * Frees every pending blob referenced by `html`. Call when a form is
 * abandoned (cancel / close without saving) — otherwise the entries, and the
 * Blobs they hold, stay alive for the lifetime of the tab, since the registry
 * deliberately outlives the editor that created them.
 *
 * Safe to call with content that was already saved: releasing is idempotent,
 * and a saved document no longer references `blob:` srcs at all.
 */
export const releasePendingImagesIn = (html?: string | null): void => {
  if (!html || !html.includes("blob:")) {
    return;
  }
  registry.forEach((_image, src) => {
    if (html.includes(src)) {
      releasePendingImage(src);
    }
  });
};
